import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Link as MuiLink,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { KeyboardEvent, useMemo, useState } from 'react';
import type { Quote } from '~/store/slices/quote/types';
import { borderWidth, radius } from '~/styles/tokens/shape';

export type JourneyQuoteProductAccordionProps = {
  quote: Quote;
  isSelected: boolean;
  isFree: boolean;
  /** Plano clube (type_club === 2) — exibe chip “Incluso” como no Figma. */
  showIncludedBadge: boolean;
  clubType: number | undefined;
  onToggle: () => void;
  /** Rótulo do preço zero. Padrão «GRÁTIS»; a renovação usa «Sem custo». */
  freeLabel?: string;
};

const priceMonthly = (value: number) =>
  new Intl.NumberFormat('pt-BR', { currency: 'BRL', style: 'currency' }).format(value);

export default function JourneyQuoteProductAccordion({
  quote,
  isSelected,
  isFree,
  showIncludedBadge,
  clubType,
  onToggle,
  freeLabel = 'GRÁTIS'
}: JourneyQuoteProductAccordionProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const shortDescriptionLen = 120;
  const descriptionLong = quote.product_description.length > shortDescriptionLen;
  const descriptionDisplay = useMemo(() => {
    if (!descriptionLong || showFullDescription) return quote.product_description;
    return `${quote.product_description.slice(0, shortDescriptionLen)}…`;
  }, [descriptionLong, quote.product_description, showFullDescription]);

  const accentHeader = expanded;

  const isFreePrice = isFree || quote.product_price === 0;
  const priceLabel = clubType === 2 ? null : isFreePrice ? freeLabel : `${priceMonthly(quote.product_price)}/mês`;

  const handleKeyToggle = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!quote.required) onToggle();
    }
  };

  return (
    <Box
      component="article"
      data-app-id={`journey-quote-product-${quote.product_id}`}
      onKeyDown={handleKeyToggle}
      role="presentation"
      sx={{
        bgcolor: alpha(theme.palette.common.white, 0.06),
        border: `${borderWidth.small}px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: `${radius.small}px`,
        mb: `${theme.spacing(2)}`,
        overflow: 'hidden',
        outline: 'none',
        transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: 200 })
      }}
    >
      <Box
        role="button"
        sx={{
          alignItems: 'center',
          cursor: quote.required ? 'default' : 'pointer',
          display: 'flex',
          gap: { sm: `${theme.spacing(2)}`, xs: `${theme.spacing(1)}` },
          justifyContent: 'space-between',
          pb: accentHeader ? `${theme.spacing(1)}` : 0,
          pt: accentHeader ? `${theme.spacing(1)}` : 0,
          px: { sm: `${theme.spacing(2)}`, xs: `${theme.spacing(1.5)}` },
          py: `${theme.spacing(2)}`
        }}
        tabIndex={quote.required ? -1 : 0}
        onClick={() => {
          if (!quote.required) onToggle();
        }}
      >
        <Box alignItems="center" display="flex" flex={1} gap={{ sm: 2, xs: 1 }} minWidth={0}>
          <Checkbox
            checked={isSelected}
            color="success"
            disabled={quote.required}
            inputProps={{
              'aria-label': `Selecionar ${quote.product_name}`
            }}
            onChange={(e) => {
              e.stopPropagation();
              if (!quote.required) onToggle();
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{
              '&.Mui-checked': { color: theme.palette.success.main },
              flexShrink: 0,
              p: 0.5
            }}
          />
          <Box alignItems="center" display="flex" flex={1} flexWrap="wrap" gap={1} minWidth={0}>
            <Typography
              component="h3"
              sx={{
                fontWeight: 600,
                minWidth: 0,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word'
              }}
              variant="subtitle2"
            >
              {quote.product_name}
            </Typography>
            {showIncludedBadge ? (
              <Chip
                color="primary"
                label="Incluso"
                size="small"
                sx={{
                  borderRadius: `${radius.medium}px`,
                  fontWeight: 500,
                  height: `${theme.spacing(4)}`
                }}
              />
            ) : null}
          </Box>
        </Box>
        <Box alignItems="center" display="flex" flexShrink={0} gap={0.5}>
          {priceLabel ? (
            <Typography
              sx={{
                fontWeight: 600,
                mr: { sm: 0.5 },
                textAlign: 'right',
                whiteSpace: 'nowrap'
              }}
              variant="subtitle2"
            >
              {priceLabel}
            </Typography>
          ) : null}
          <IconButton
            aria-expanded={expanded}
            aria-label={expanded ? 'Recolher detalhes' : 'Expandir detalhes'}
            data-app-id={`journey-quote-expand-${quote.product_id}`}
            edge="end"
            size="medium"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            sx={{ color: 'text.secondary' }}
          >
            <KeyboardArrowDownIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: theme.transitions.create('transform')
              }}
            />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box pb={2} px={2}>
          <Typography component="div" sx={{ fontWeight: 400, mb: 0.5 }} variant="body1">
            {descriptionDisplay}
            {descriptionLong ? (
              <MuiLink
                component="button"
                sx={{ color: 'primary.main', fontWeight: 500, ml: 1, verticalAlign: 'baseline' }}
                type="button"
                underline="none"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullDescription((v) => !v);
                }}
              >
                {showFullDescription ? 'Ver menos' : 'Ver mais'}
              </MuiLink>
            ) : null}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography gutterBottom sx={{ fontWeight: 600 }} variant="body2">
            Coberturas inclusas
          </Typography>
          {quote.cover.slice(0, 6).map((c) => (
            <Typography
              color="text.secondary"
              component="div"
              key={`${quote.product_id}-${c.cover_name}`}
              sx={{ alignItems: 'flex-start', display: 'flex', gap: 1, py: 0.25 }}
              variant="body2"
            >
              <CheckRoundedIcon color="success" sx={{ fontSize: 18, mt: 0.15 }} />
              {c.cover_name}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
